/**
 * System Page — observabilidad para SUPER_ADMIN.
 *
 * Muestra el estado de los componentes criticos del producto: base de
 * datos, almacenamiento, schedulers. Reutiliza los KPIs ya disponibles
 * del endpoint /admin/dashboard/business-kpis.
 */

import { Activity, Database, HardDrive, Server, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { useBusinessKpis } from '@/application/admin-dashboard/useAdminDashboard'

const HealthBadge = ({ status }: { status: 'ok' | 'degraded' | 'error' }) => {
  const cls =
    status === 'ok'
      ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200'
      : status === 'degraded'
        ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200'
        : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'
  const label = status === 'ok' ? 'Operativo' : status === 'degraded' ? 'Degradado' : 'Error'
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}
    >
      {label}
    </span>
  )
}

export default function SystemPage() {
  const { data, isLoading } = useBusinessKpis()

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
      </div>
    )
  }

  const { systemHealth, alerts } = data

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Activity className="h-6 w-6" />
          Salud del sistema
        </h1>
        <p className="text-[var(--foreground-muted)]">
          Estado en vivo de los componentes criticos del producto.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Database className="h-4 w-4" />
              Base de datos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <HealthBadge status={systemHealth.database} />
            <p className="text-xs text-[var(--foreground-muted)] mt-2">
              PostgreSQL — backbone de toda la aplicacion.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <HardDrive className="h-4 w-4" />
              Almacenamiento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <HealthBadge status={systemHealth.storage} />
            <p className="text-xs text-[var(--foreground-muted)] mt-2">
              MinIO/S3 — DICOM, thumbnails y adjuntos.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Server className="h-4 w-4" />
              Schedulers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {systemHealth.schedulers.map((s) => (
                <div key={s.name} className="flex items-center justify-between text-sm">
                  <span className="text-[var(--foreground-muted)]">{s.name}</span>
                  <HealthBadge status={s.isRunning ? 'ok' : 'error'} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {alerts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Alertas activas</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {alerts.map((a, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span
                    className={`inline-block w-2 h-2 rounded-full mt-1.5 ${
                      a.severity === 'critical'
                        ? 'bg-red-500'
                        : a.severity === 'warning'
                          ? 'bg-yellow-500'
                          : 'bg-blue-500'
                    }`}
                  />
                  <div>
                    <p className="font-medium">{a.title}</p>
                    <p className="text-[var(--foreground-muted)]">{a.message}</p>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
